import { styled } from "styled-components";
import { atom, useAtom, } from "jotai";
import { cloneElement, isValidElement } from "react";
import { getTheme, themed, } from "@bracket/ui/themes";
import { useEffect, useRef, type ReactNode, type MouseEvent, } from "react";
import { Flow, UIBase, } from "@bracket/ui/nodes";
import { SelectItemProps } from "./SelectItem";
import { SelectButton, } from "./SelectButton";
import { selections, type SelectionAtomState, } from "./atomic";
import { SelectableItem, type SelectableItemProps, } from "./SelectableItem";


export declare type OnSelectHandler<T extends object = object> = ( event: MouseEvent<HTMLDivElement>, value: T ) => void;
export declare type SelectionProps<T extends object = object> = {
	id: string;
	children: ReactNode;
	initial?: string;
	onSelect?: OnSelectHandler<T>;
}



export const Select = <T extends object = object>({
	id,
	initial,
	children,
	onSelect,
}: SelectionProps<T>) => {
	themed();

	const selectables = Array.isArray(children)
		? children.map((child, i, arr) => {
				if(isValidElement(child)) {
					const { value, label, } = child.props;
					const key = JSON.stringify({ value, label, });
					return <SelectableItem key={key} parent={id} onSelect={onSelect} item={{ value, label, sid: fastHash(key), }} />;
				}
				throw new Error("Select expects an array of children");
			})
		: (isValidElement(children) ? <SelectableItem parent={id} position="single" onSelect={onSelect} /> : children);

	if(!selectables || !Array.isArray(selectables))
		throw new Error("Select expects an array of children");

	if(initial && !selectables.some((child: React.ReactElement<SelectItemProps>) => (child.props.value || child.props.item?.value) === initial))
		throw new Error("Initial value is not a valid selectable");

	if(!(id in selections))
		selections[id] = atom<SelectionAtomState>({
			open: false,
			values: (
				selectables.map(( child: React.ReactElement<SelectableItemProps> ) => {
					return child.props.item?.value;
				}) as string[]
			),
			selected: selectables.find((child: React.ReactElement<SelectableItemProps>) => (child.props.value || child.props.item?.value) === initial)?.props.item ?? null,
			width: -1,
			height: -1,
		});

	const [selection, setSelection] = useAtom(selections[id]);
	const { open, selected, width, height, } = selection;

	const activate = () => {
		setSelection({ ...selection, open: !open, });
	}

	const listRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		const { width, } = selection;
		if(width === -1) {
			if(listRef.current) {
				const { width, } = listRef.current.getBoundingClientRect();
				setSelection({ ...selection, width: width + 60, });
			}
		}
	}, [width]);

	return (
		<SelectContainer>
			<SelectButton onClick={activate} {...{ width, open, selected, height: selection.height, }} />
			<SelectBrace ref={listRef}>
				<SelectList $open={open} >
					{ selection.width === -1 && selectables.map(( child: React.ReactElement<SelectItemProps>, i, arr ) => {
								return cloneElement(child, {
									position: (
										i === 0 && arr.length > 1 ? "first" : 
										i === arr.length - 1 && arr.length > 1 ? "last" :
										arr.length - 1 === i ? "last" : "middle"
									),
								});
							}) }
					{ selection.width > 0 && open &&
						selectables
							.filter((child: React.ReactElement<SelectItemProps>) => selected.sid !== child.props.item.sid)
							.map(( child: React.ReactElement<SelectItemProps>, i, arr ) => {
								return cloneElement(child, {
									position: (
										i === 0 && arr.length > 1 ? "first" : 
										i === arr.length - 1 && arr.length > 1 ? "last" :
										arr.length - 1 === i ? "last" : "middle"
									),
								});
							})
					}
				</SelectList>
			</SelectBrace>
		</SelectContainer>
	)
}



const SelectContainer = styled(UIBase).attrs({ $padding: "0rem", $margin: "0rem", })`
	user-select: none;
	box-shadow: 0 0 10px 0 rgba(0, 2, 0, 0.1);
	align-items: center;
`;


const SelectBrace = styled(Flow).attrs({ $flexible: true, })`
	position: relative;
	flex-direction: column;
	flex: 1;
	width: 100%;
	justify-content: center;
`;


const SelectList = styled(Flow)<{ $open: boolean; $width: string; $height: string; }>`
	position: absolute;
	max-height: ${({ $open }) => $open ? "100%" : "0%"};
	width: 100%;
	flex: 1;
	flex-direction: column;
	align-items: center;
`;


function fastHash( str: string ): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char; // hash * 31 + char
    hash |= 0; // Ensure 32-bit integer
  }
  return (hash >>> 0).toString(); // Ensure unsigned 32-bit integer
}
